/**
 * The breadcrumb's last crumb on a DETAIL page — "Account › Accounts ›
 * HDFC/Savings-Pritesh". The frame knows the module and section from the path
 * alone (`modules.ts`); only the page knows its entity's name, so it reports
 * it here:
 *
 *     useBreadcrumbTail(account?.name);
 *
 * Each caller registers under its own id and the NEWEST registration wins, so
 * two copies of a page alive at once (the screen transition keeps the leaving
 * copy mounted while it animates out) cannot wipe each other's name when one
 * unmounts. Outside the shell (tests, public pages) the hook is a no-op.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  type ReactNode,
} from "react";

interface CrumbTail {
  tail: string | null;
  set(id: string, label: string | null): void;
}

const Ctx = createContext<CrumbTail | null>(null);

export function BreadcrumbTailProvider({ children }: { children: ReactNode }) {
  // Insertion-ordered: re-setting an id moves it to the end (newest).
  const [entries, setEntries] = useState<[string, string][]>([]);
  const set = useCallback((id: string, label: string | null) => {
    setEntries((prev) => {
      const rest = prev.filter(([k]) => k !== id);
      return label ? [...rest, [id, label]] : rest;
    });
  }, []);
  const tail = entries.length ? entries[entries.length - 1][1] : null;
  return <Ctx.Provider value={{ tail, set }}>{children}</Ctx.Provider>;
}

export function useBreadcrumbTailValue(): string | null {
  return useContext(Ctx)?.tail ?? null;
}

export function useBreadcrumbTail(label: string | null | undefined): void {
  const set = useContext(Ctx)?.set;
  const id = useId();
  useEffect(() => {
    if (!set) return;
    set(id, label || null);
    return () => set(id, null);
  }, [set, id, label]);
}
